import { useState } from 'react'

import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import PageLoading from 'modules/layout/components/Page/Loading'
import {
  ResultValidation,
  useResultValidations,
} from 'modules/payroll_bundle/hooks/useResultValidations'
import { ResultValidationStatus } from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import Validation, {
  colorsForValidationStatus,
} from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/_components/Validation'
import Formula from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/_components/_Formula'

interface Props {
  open: boolean
  onClose: () => void
  currency: string
  employeeIds: string[]
}

const ValidationsSidePanel = ({ open, onClose, currency }: Props) => {
  const { currentPeriod } = usePolicyPeriods()
  const { data, fetching } = useResultValidations(currentPeriod?.id)
  const [openTooltip, toggleTooltip] = useToggle(false)

  return (
    <Sidepanel
      overlay={false}
      boxPadding={false}
      width="wide"
      title={
        <Box flexDirection="row" gap="s12">
          <Icon.Medium icon="validation" color="grey800" />
          <Text size="300" color="grey800" weight="semibold">
            {i18n.t('payroll_bundle.results_payments.results.validations.sidepanel_title')}
          </Text>
          <Tooltip
            color="grey800"
            type="primary"
            attachment="bottom center"
            targetAttachment="top center"
            size="big"
            open={openTooltip}
            toggle={toggleTooltip}
          >
            <Icon.Medium icon="info-circle-line" color="grey800" />
            <Tooltip.Pad>
              {i18n.t('payroll_bundle.results_payments.results.validations.sidepanel_tooltip')}
            </Tooltip.Pad>
          </Tooltip>
        </Box>
      }
      onClose={onClose}
      open={open}
      closeLabel={i18n.t('common.close')}
    >
      {fetching && <PageLoading />}
      {!fetching && <ValidationsList resultValidations={data} currency={currency} />}
    </Sidepanel>
  )
}

export default ValidationsSidePanel

interface ValidationsListProps {
  resultValidations: ResultValidation[]
  currency: string
}

const ValidationsList = ({ resultValidations, currency }: ValidationsListProps) => {
  const resultValidationGroups = _.chain(resultValidations)
    .groupBy((validationItem) => validationItem.validation?.name)
    .value()

  const data = Object.entries(resultValidationGroups)

  return (
    <Box padding="s24" width="full" gap="s24" alignItems="center">
      <Box width="full" gap="s24">
        {data.map((group) => {
          return (
            <ValidationGroup key={group[0]} name={group[0]} items={group[1]} currency={currency} />
          )
        })}
      </Box>
    </Box>
  )
}

interface ValidationGroupProps {
  name: string
  items: ResultValidation[]
  currency: string
}

const ValidationGroup = ({ name, items, currency }: ValidationGroupProps) => {
  const [showFullList, toggleShowFullList] = useToggle(false)
  const [showFormula, toggleShowFormula] = useState<boolean>(false)
  const { currentPeriod } = usePolicyPeriods()
  const LIST_LENGTH = 5

  const firstError = _.find(items, (item) => item.status == ResultValidationStatus.invalid)
  const firstWarning = _.find(items, (item) => item.status == ResultValidationStatus.warning)
  const groupStatus = firstError
    ? ResultValidationStatus.invalid
    : firstWarning
      ? ResultValidationStatus.warning
      : ResultValidationStatus.valid
  const diffTotal = _.reduce(items, (sum, item) => (sum += item.difference), 0)
  const notValidValidations = items.filter((item) => item.status != ResultValidationStatus.valid)
  const employeeIds = _.reduce<ResultValidation[], string[]>(
    notValidValidations,
    (list, item) => [...list, (item as ResultValidation).employee!.id],
    []
  )
  const lastPosition = notValidValidations.length - 1

  const listToggleText = showFullList
    ? i18n.t('payroll_bundle.results_payments.results.validations.collapse')
    : i18n.t('payroll_bundle.results_payments.results.validations.see_more')

  return (
    <Box
      flexDirection="column"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        border={{
          bottom:
            groupStatus != ResultValidationStatus.valid
              ? { color: 'grey400', style: 'solid', width: 's1' }
              : undefined,
        }}
      >
        <Box paddingX="s16" paddingY="s12">
          <Box flexDirection="row" gap="s12" alignItems="center">
            <Box>
              <Icon.Small
                encircled={colorsForValidationStatus(groupStatus)?.outline}
                icon="circle"
                color={colorsForValidationStatus(groupStatus)?.color}
              />
            </Box>
            <Text.Normal weight="medium" color="grey1000">
              {name}
            </Text.Normal>
          </Box>
        </Box>
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="flexEnd"
          gap="s12"
          padding="s12"
        >
          <Text.Normal
            color={firstError ? 'radicalRed1200' : firstWarning ? 'champagne1000' : 'viridian1000'}
            size="100"
          >
            {groupStatus != ResultValidationStatus.valid ? formatAmount(diffTotal, currency) : ''}
          </Text.Normal>
          <Button
            type="secondary"
            onClick={() => toggleShowFormula((prevShowFormula) => !prevShowFormula)}
            iconName="calculator"
          />
        </Box>
      </Box>

      {showFormula && (
        <>
          {groupStatus === ResultValidationStatus.valid && (
            <Box border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}></Box>
          )}

          <Formula
            policyPeriodId={currentPeriod?.id}
            validationFormula={_.first(items)!.validation!}
            employeeIds={employeeIds}
            currency={currency}
          />

          {groupStatus !== ResultValidationStatus.valid && (
            <Box border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}></Box>
          )}
        </>
      )}

      <Box
        overflowY="hidden"
        overflowX="hidden"
        height={
          notValidValidations.length > LIST_LENGTH
            ? showFullList
              ? 'fit-content'
              : 's300'
            : 'fit-content'
        }
      >
        {notValidValidations
          .sort((a, b) => b.difference - a.difference)
          .map((item, index) => (
            <Validation
              key={item.employee?.id}
              item={item}
              last={index === lastPosition}
              currency={currency}
            />
          ))}
      </Box>
      {notValidValidations.length > LIST_LENGTH && (
        <Box onClick={toggleShowFullList(!showFullList)} background="grey200">
          <Box paddingX="s16" paddingY="s8" gap="s4" flexDirection="row" alignItems="center">
            <Text size="200" color="grey800" weight="medium">
              {listToggleText}
            </Text>
            <Icon.Small icon={showFullList ? 'chevron-up' : 'chevron-down'} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
